import React, { useEffect, useState } from 'react';

interface EmbeddingVisualizationSVGProps {
  embeddingData: number[];
  iterations: number;
  graphSliderValue: number;
  baseColor: string;
  optimizationProgress?: number;
  iterationResults?: Array<{
    prompt: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    iteration: number;
    seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
  }>;
  isMultiAgent?: boolean;
}

const EmbeddingVisualizationSVG: React.FC<EmbeddingVisualizationSVGProps> = ({
  embeddingData,
  iterations,
  graphSliderValue,
  baseColor,
  optimizationProgress = 0,
  iterationResults,
  isMultiAgent = false,
}) => {
  const [dimensions, setDimensions] = useState(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width >= 1200) {
        return { width: 450, height: 350, padding: 60 };
      } else if (width >= 768) {
        return { width: 400, height: 300, padding: 50 };
      } else if (width >= 480) {
        return { width: 350, height: 250, padding: 50 };
      } else {
        return { width: 300, height: 200, padding: 50 };
      }
    }
    return { width: 400, height: 300, padding: 50 };
  });

  // Обновляем размеры при изменении размера окна
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1200) {
        setDimensions({ width: 450, height: 350, padding: 60 });
      } else if (width >= 768) {
        setDimensions({ width: 400, height: 300, padding: 50 });
      } else if (width >= 480) {
        setDimensions({ width: 350, height: 250, padding: 50 });
      } else {
        setDimensions({ width: 300, height: 200, padding: 50 });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { width, height, padding } = dimensions;
  const chartWidth = width - 2 * padding;
  const chartHeight = height - 2 * padding;
  const originX = padding;
  const originY = height - padding;

  // Показываем точки только на основе реальных данных от бэкенда
  const maxVisiblePoints = iterationResults ? iterationResults.length : 0;

  // Generate points for the 2D embedding space
  const generateSnailPoints = (numVisiblePoints: number) => {
    const points = [];

    // Если есть данные с бэкенда, используем их для расчета координат
    if (iterationResults && iterationResults.length > 0) {
      const visibleResults = [...iterationResults].sort((a, b) => {
        const ai = a.seq !== undefined ? a.seq : (a.iteration !== undefined ? a.iteration : 0);
        const bi = b.seq !== undefined ? b.seq : (b.iteration !== undefined ? b.iteration : 0);
        return ai - bi;
      });
      
      // Находим диапазон значений для масштабирования
      const embeddingXValues = visibleResults
        .filter(r => r.embedding_point)
        .map(r => r.embedding_point!.x);
      const embeddingYValues = visibleResults
        .filter(r => r.embedding_point)
        .map(r => r.embedding_point!.y);
      
      let minX = 0, maxX = 1, minY = 0, maxY = 1;
      
      if (embeddingXValues.length > 0) {
        minX = Math.min(...embeddingXValues);
        maxX = Math.max(...embeddingXValues);
        // Добавляем минимальный диапазон, чтобы точки не застревали
        if (maxX === minX) {
          maxX = minX + 0.1;
          minX = minX - 0.05;
        }
      }
      
      if (embeddingYValues.length > 0) {
        minY = Math.min(...embeddingYValues);
        maxY = Math.max(...embeddingYValues);
        // Добавляем минимальный диапазон, чтобы точки не застревали
        if (maxY === minY) {
          maxY = minY + 0.1;
          minY = minY - 0.05;
        }
      }
      
      const xRange = maxX - minX;
      const yRange = maxY - minY;
      
      for (let i = 0; i < visibleResults.length; i++) {
        const result = visibleResults[i];
        if (result && result.embedding_point) {
          // Используем seq или iteration для правильной нумерации
          // seq: 0 -> iterationNumber: 0, seq: 1 -> iterationNumber: 1, etc.
          const iterationNumber = result.seq !== undefined ? result.seq : (result.iteration !== undefined ? result.iteration : (isMultiAgent ? i + 1 : i));
          
          // Нормализуем координаты к диапазону графика
          const normalizedX = xRange > 0 ? (result.embedding_point.x - minX) / xRange : 0.5;
          const normalizedY = yRange > 0 ? (result.embedding_point.y - minY) / yRange : 0.5;
          
          const x = originX + normalizedX * chartWidth;
          const y = originY - normalizedY * chartHeight; // Инвертируем Y-ось
          
          // Ограничиваем координаты границами графика с небольшим отступом
          const clampedX = Math.max(padding + 10, Math.min(width - padding - 10, x));
          const clampedY = Math.max(padding + 10, Math.min(height - padding - 10, y));
          
          points.push({ 
            x: clampedX, 
            y: clampedY, 
            originalIndex: i,
            iterationNumber: iterationNumber,
            embedding1: result.embedding_point.x,
            embedding2: result.embedding_point.y
          });
        } else {
          // Если нет embedding_point — пропускаем точку (не рисуем синтетические точки)
          continue;
        }
      }
    } else {
      // Если нет данных с бэкенда, не показываем точки
      return [];
    }
    
    return points;
  };

  // Generate only visible points based on optimization progress
  const visibleSnailPoints = generateSnailPoints(maxVisiblePoints);

  // Create separate path segments for different colors
  const createColoredPathSegments = () => {
    const segments = [];
    
    // Сортируем точки по originalIndex для правильного порядка
    const sortedPoints = [...visibleSnailPoints].sort((a, b) => a.originalIndex - b.originalIndex);
    
    // Создаем сегменты только между последовательными точками
    for (let i = 0; i < sortedPoints.length - 1; i++) {
      const currentPoint = sortedPoints[i];
      const nextPoint = sortedPoints[i + 1];
      
      // Проверяем, что это последовательные точки по iterationNumber
      const currentIter = currentPoint.iterationNumber ?? currentPoint.originalIndex;
      const nextIter = nextPoint.iterationNumber ?? nextPoint.originalIndex;
      if (nextIter === currentIter + 1) {
        // Логика подсветки сегментов: учитываем точку 0 (первый ответ) и оптимизированные промпты
        // graphSliderValue указывает на выбранную итерацию оптимизированного промпта (0-3 для 4 итераций)
        // Сегмент подсвечивается, если следующая точка должна быть подсвечена
        const currentIterationNumber = currentIter;
        const nextIterationNumber = nextIter;
        const isSegmentHighlighted = currentIterationNumber === 0 || (nextIterationNumber > 0 && nextIterationNumber <= graphSliderValue + 1);
        
        segments.push({
          pathData: `M${currentPoint.x},${currentPoint.y} L${nextPoint.x},${nextPoint.y}`,
          color: isSegmentHighlighted ? '#FF8300' : '#0099FA'
        });
      }
    }
    
    return segments;
  };

  const pathSegments = createColoredPathSegments();

  return (
    <svg 
      width="100%" 
      height="100%" 
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ 
        maxWidth: '100%', 
        height: 'auto',
        minHeight: '250px'
      }}
    >
      {/* Axes */}
      <line 
        x1={padding} 
        y1={originY} 
        x2={width - padding} 
        y2={originY} 
        stroke="#0A2A47" 
        strokeWidth={width >= 480 ? 1 : 0.5} 
      />
      <line 
        x1={padding} 
        y1={originY} 
        x2={padding} 
        y2={padding} 
        stroke="#0A2A47" 
        strokeWidth={width >= 480 ? 1 : 0.5} 
      />

      {/* X-axis ticks and labels */}
      {(() => {
        const ticks = [];
        const maxTicks = 5;
        
        // Находим диапазон значений для оси X
        let minX = 0, maxX = 1;
        if (iterationResults && iterationResults.length > 0) {
          const embeddingXValues = iterationResults
            .filter(r => r.embedding_point)
            .map(r => r.embedding_point!.x);
          if (embeddingXValues.length > 0) {
            minX = Math.min(...embeddingXValues);
            maxX = Math.max(...embeddingXValues);
            // Добавляем минимальный диапазон для подписей
            if (maxX === minX) {
              maxX = minX + 0.1;
              minX = minX - 0.05;
            }
          }
        }
        
        for (let i = 0; i <= maxTicks; i++) {
          const x = padding + (chartWidth * i) / maxTicks;
          
          // Рассчитываем значение на основе реального диапазона
          const value = minX + (maxX - minX) * (i / maxTicks);
          
          ticks.push(
            <React.Fragment key={`x-tick-${i}`}>
              <line 
                x1={x} 
                y1={originY} 
                x2={x} 
                y2={originY + (width >= 480 ? 5 : 3)} 
                stroke="#0A2A47" 
                strokeWidth={width >= 480 ? 1 : 0.5} 
              />
              <text 
                x={x} 
                y={originY + (width >= 480 ? 20 : 15)} 
                textAnchor="middle" 
                fill="#0A2A47" 
                fontSize={width >= 1200 ? 14 : width >= 768 ? 12 : width >= 480 ? 11 : 10}
                fontWeight="600"
              >
                {typeof value === 'number' ? value.toFixed(2) : value}
              </text>
            </React.Fragment>
          );
        }
        return ticks;
      })()}

      {/* Y-axis ticks and labels */}
      {(() => {
        const ticks = [];
        const maxTicks = 5;
        
        // Находим диапазон значений для оси Y
        let minY = 0, maxY = 1;
        if (iterationResults && iterationResults.length > 0) {
          const embeddingYValues = iterationResults
            .filter(r => r.embedding_point)
            .map(r => r.embedding_point!.y);
          if (embeddingYValues.length > 0) {
            minY = Math.min(...embeddingYValues);
            maxY = Math.max(...embeddingYValues);
            // Добавляем минимальный диапазон для подписей
            if (maxY === minY) {
              maxY = minY + 0.1;
              minY = minY - 0.05;
            }
          }
        }
        
        for (let i = 0; i <= maxTicks; i++) {
          const y = originY - (chartHeight * i) / maxTicks;
          
          // Рассчитываем значение на основе реального диапазона
          const value = minY + (maxY - minY) * (i / maxTicks);
          
          ticks.push(
            <React.Fragment key={`y-tick-${i}`}>
              <line 
                x1={originX} 
                y1={y} 
                x2={originX - (width >= 480 ? 5 : 3)} 
                y2={y} 
                stroke="#0A2A47" 
                strokeWidth={width >= 480 ? 1 : 0.5} 
              />
              <text 
                x={originX - (width >= 1200 ? 20 : width >= 768 ? 18 : width >= 480 ? 15 : 12)} 
                y={y + (width >= 1200 ? 4 : width >= 768 ? 3 : width >= 480 ? 2 : 1)} 
                textAnchor="end" 
                fill="#0A2A47" 
                fontSize={width >= 1200 ? 14 : width >= 768 ? 12 : width >= 480 ? 11 : 10}
                fontWeight="600"
              >
                {typeof value === 'number' ? value.toFixed(2) : value}
              </text>
            </React.Fragment>
          );
        }
        return ticks;
      })()}

      {/* X-axis label */}
      <text 
        x={width / 2} 
        y={height - padding / 6} 
        textAnchor="middle" 
        fill="#0A2A47" 
        fontSize={width >= 1200 ? 18 : width >= 768 ? 16 : width >= 480 ? 14 : 12}
        fontWeight="500"
      >
        Эмбеддинг
      </text>
      {/* Y-axis label */}
      <text 
        x={padding / 6} 
        y={height / 2} 
        textAnchor="middle" 
        transform={`rotate(-90 ${padding / 6} ${height / 2})`} 
        fill="#0A2A47" 
        fontSize={width >= 1200 ? 18 : width >= 768 ? 16 : width >= 480 ? 14 : 12}
        fontWeight="500"
      >
        Эмбеддинг
      </text>

      {/* Simple line segments */}
      {pathSegments.map((segment, index) => (
        <path
          key={index}
          d={segment.pathData}
          fill="none"
          stroke={segment.color}
          strokeWidth={width >= 480 ? 2 : 1.5}
          style={{ 
            transition: 'stroke 0.3s ease-in-out',
            animation: `drawLine 0.5s ease-out ${index * 0.1}s both`
          }}
        />
      ))}

      {/* Simple points */}
      {visibleSnailPoints.map((point, index) => {
        // Логика подсветки: учитываем точку 0 (первый ответ) и оптимизированные промпты
        // graphSliderValue указывает на выбранную итерацию оптимизированного промпта (0-3 для 4 итераций)
        // Точка 0 всегда подсвечена, остальные точки подсвечиваются в зависимости от выбранной итерации
        const iterationNumber = point.iterationNumber !== undefined ? point.iterationNumber : point.originalIndex;
        const isHighlighted = iterationNumber === 0 || (iterationNumber > 0 && iterationNumber <= graphSliderValue + 1);
        const pointColor = isHighlighted ? '#FF8300' : '#0099FA';
        
        return (
          <React.Fragment key={index}>
            {/* Simple point */}
            <circle
              cx={point.x}
              cy={point.y}
              r={width >= 480 ? 4 : 3}
              fill={pointColor}
              stroke="none"
              style={{ 
                transition: 'all 0.3s ease-in-out',
                animation: `fadeInScale 0.6s ease-out ${index * 0.15}s both`
              }}
            />
            
            {/* Point number */}
            <text
              x={point.x + 10}
              y={point.y + 5}
              fontSize={width >= 1200 ? 10 : width >= 768 ? 9 : width >= 480 ? 8 : 7}
              fill="#0A2A47"
              fontWeight="bold"
              style={{ 
                transition: 'all 0.3s ease-in-out',
                animation: `fadeInText 0.4s ease-out ${index * 0.15 + 0.2}s both`
              }}
            >
              {iterationNumber}
            </text>
          </React.Fragment>
        );
      })}

      <style>
        {`
          @keyframes drawLine {
            from {
              stroke-dasharray: 1000;
              stroke-dashoffset: 1000;
            }
            to {
              stroke-dashoffset: 0;
            }
          }
          
          @keyframes fadeInScale {
            from {
              opacity: 0;
              transform: scale(0);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          
          @keyframes fadeInText {
            from {
              opacity: 0;
              transform: translateY(-5px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </svg>
  );
};

export default EmbeddingVisualizationSVG; 